import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const FAQ_ITEMS = [
  {
    question: 'How does the membership work?',
    answer: 'Sign up for free and receive $50 in your wallet instantly. This is your membership credit—no recurring fees, no hidden charges. Top up in multiples of $500 whenever you need to shop.',
  },
  {
    question: 'Are there any membership or transaction fees?',
    answer: 'No. There are no membership fees, no transaction fees, and no hidden charges. Every dollar you add goes directly to your wallet. What you see is what you pay.',
  },
  {
    question: 'Are there card transaction fees when I top up?',
    answer: 'Card processing may be subject to standard payment provider fees when you add funds to your wallet. Any applicable fees will be shown clearly before you confirm your top-up. Once funds are in your wallet, there are no additional transaction fees when you shop.',
  },
  {
    question: 'How much can I save as a member?',
    answer: 'Members save up to 20% compared to non-member pricing. Every product shows member and non-member prices so you can see your savings before you buy.',
  },
  {
    question: 'How do I top up my wallet?',
    answer: 'You can add funds to your wallet in multiples of $500 anytime from your dashboard. Your balance is always available for shopping—no need to enter payment details at checkout.',
  },
  {
    question: 'Can I track my orders and spending?',
    answer: 'Yes. View your complete order history, track purchases, and manage your wallet balance from one convenient dashboard. Everything is transparent and easy to manage.',
  },
];

const About: React.FC = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="container">
          <h1>About Club Kazi</h1>
          <p className="hero-tagline">Zero fees for the best prices</p>
          <p className="hero-description">
            Your exclusive membership grocery store where quality meets affordability. 
            No hidden fees, no membership charges beyond your initial wallet credit.
          </p>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How Club Kazi Works</h2>
          <div className="steps-container">
            <div className="step-card">
              <div className="step-icon">1️⃣</div>
              <h3>Join as a Member</h3>
              <p>
                Sign up for free and receive $50 in your wallet instantly. 
                This is your membership credit - no recurring fees, no hidden charges.
              </p>
            </div>
            <div className="step-card">
              <div className="step-icon">2️⃣</div>
              <h3>Top Up Your Wallet</h3>
              <p>
                Add funds to your wallet in multiples of $500. 
                Your wallet balance is always available for shopping - top up anytime, anywhere.
              </p>
            </div>
            <div className="step-card">
              <div className="step-icon">3️⃣</div>
              <h3>Shop Premium Products</h3>
              <p>
                Browse our curated selection of high-quality groceries. 
                Every product shows member pricing and non-member pricing so you can see your savings.
              </p>
            </div>
            <div className="step-card">
              <div className="step-icon">4️⃣</div>
              <h3>Checkout with Ease</h3>
              <p>
                Add items to your cart and checkout seamlessly. 
                Purchases are automatically deducted from your wallet - no credit cards needed at checkout.
              </p>
            </div>
            <div className="step-card">
              <div className="step-icon">5️⃣</div>
              <h3>Track Your Orders</h3>
              <p>
                View your complete order history, track purchases, and manage your wallet balance. 
                Everything is transparent and easy to manage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - same accordion as Order History */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="orders-list">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openFaqIndex === index;
              return (
                <div key={index} className="order-card">
                  <button
                    type="button"
                    className="order-summary"
                    onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                  >
                    <div>
                      <div className="order-summary-title">{item.question}</div>
                    </div>
                    <div className="order-summary-right">
                      <div className="order-summary-toggle">
                        {isOpen ? 'Hide details' : 'View details'}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div
                      id={`faq-answer-${index}`}
                      className="order-details"
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                    >
                      <p className="faq-answer-text">{item.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Club Kazi?</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">💳</div>
              <h3>Digital Wallet System</h3>
              <p>
                Manage your funds with our convenient wallet system. 
                Top up in multiples of $500 and shop without worrying about payment methods.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📊</div>
              <h3>Price Transparency</h3>
              <p>
                See member prices and non-member prices side by side. 
                Know exactly how much you're saving on every purchase.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🚀</div>
              <h3>Fast & Secure</h3>
              <p>
                Quick checkout process with secure wallet transactions. 
                No need to enter payment details every time you shop.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">📱</div>
              <h3>Easy Management</h3>
              <p>
                Track your orders, view transaction history, and manage your wallet 
                all from one convenient dashboard.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🎯</div>
              <h3>Premium Selection</h3>
              <p>
                Curated selection of high-quality groceries. 
                We focus on quality products at the best member prices.
              </p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">🔒</div>
              <h3>Secure & Private</h3>
              <p>
                Your data and transactions are secure. 
                We prioritize your privacy and security in everything we do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Experience Club Kazi?</h2>
          <p>Join today and start saving with zero fees and the best prices</p>
          <div className="cta-buttons">
            <Link to="/register" className="btn btn-primary btn-large">
              Become a Member
            </Link>
            <Link to="/products" className="btn btn-outline btn-large">
              Browse Products
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
